import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { alpha, Box, Paper, Stack } from '@rossum/ui/material';
import { SIDEBAR_WIDTH } from '../../constants/values';
import makeResizable from '../../decorators/makeResizable';
import { useCanvasDimensions } from '../../features/annotation-view/document-canvas-svg/useCanvasDimensions';
import { useDocumentStore } from '../../features/annotation-view/document-store/DocumentStore';
import { visiblePageZustandSelector } from '../../features/annotation-view/document-store/documentStoreSelectors';
import { pagesSelector } from '../../redux/modules/annotation/selectors';
import {
  currentMultivalueDatapointSelector,
  datapointPathSelector,
  getTableDatapoints,
} from '../../redux/modules/datapoints/selector';
import { suggestedOperationsOptionsSelector } from '../../redux/modules/datapoints/suggestedOperations/selector';
import { isVirtualDatapoint } from '../../redux/modules/datapoints/typedHelpers';
import {
  footerLayoutSelector,
  sortFooterColumnsSelector,
} from '../../redux/modules/user/selectors';
import { State } from '../../types/state';
import { AggregationForCurrentColumn } from './AggregationForCurrentColumn';
import { PasteGridButton } from './components/PasteGridButton';
import SearchAndReplace from './components/SearchAndReplace';
import { SuggestedOperationsActions } from './components/SuggestedOperationsActions';
import { FooterTableList } from './FooterTableList';
import styles from './style.module.sass';

type FooterProps = {
  // AFI: this is optional because makeResizable seems to have wrong types -> revisit later
  height?: number;
};

// Should match $footerZIndex SASS variable
const FOOTER_Z_INDEX = 10003;

export const Footer = ({ height }: FooterProps) => {
  const pages = useSelector(pagesSelector);
  const dimensions = useCanvasDimensions(pages);
  const layout = useSelector(footerLayoutSelector);
  const sortFooterColumns = useSelector(sortFooterColumnsSelector);
  const scrollRef = useRef<HTMLElement>();
  const currentMultivalue = useSelector(currentMultivalueDatapointSelector);

  const { pageNumber: visiblePageNumberSVG } = useDocumentStore(
    visiblePageZustandSelector(dimensions)
  );

  const visiblePageNumber = visiblePageNumberSVG;

  const readOnly = useSelector(
    (state: State) =>
      state.ui.readOnly ||
      suggestedOperationsOptionsSelector(state).rowsCount > 0 ||
      !!state.ui.actionInProgress
  );

  useEffect(() => {
    // Skip initial render - scroll only when user changes the layout.
    // Otherwise it doesn't work properly, when a footer is opened
    // with already selected datapoint.
    if (layout) {
      scrollRef.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, [layout]);

  if (
    currentMultivalue === null ||
    currentMultivalue.meta.isSimpleMultivalue === true
  ) {
    return null;
  }

  return (
    <Stack
      id="footer"
      direction="column"
      alignItems="center"
      style={{ height: `${height}px` }}
      sx={{
        // FOOTER_Z_INDEX - 1 is so that the slide animation of FooterSelectionPanel does not overflow on the sidebar.
        zIndex: FOOTER_Z_INDEX - 1,
        borderTop: t =>
          `1px solid ${
            t.palette.mode === 'dark'
              ? alpha(t.palette.common.white, 0.12)
              : alpha(t.palette.common.black, 0.12)
          }`,
      }}
    >
      {/* In principle, these styles could be on the parent, but it seems it doesn't work properly with overflow */}
      <Box
        ref={scrollRef}
        sx={{
          // This css property prevents f.e. browser navigation with swipe gesture when user reaches the end of the container
          overscrollBehaviorX: 'contain',
          overflow: 'scroll',
          scrollbarColor: 'auto',
          [`&::-webkit-scrollbar`]: {
            width: 12,
            height: 12,
          },
          zIndex: 1,

          width: '100%',
          height: '100%',
          // background color is here, so that this element can cover suggested operations panel
          backgroundColor: t => t.palette.background.paper,
        }}
      >
        <FooterTableList
          layout={layout}
          sortFooterColumns={sortFooterColumns}
          scrolledElement={scrollRef.current}
          readOnly={readOnly}
          currentMultivalue={currentMultivalue}
        />
      </Box>
      <SuggestedOperationsActionsWrapper />
      {visiblePageNumber && <PasteGridButton pageNumber={visiblePageNumber} />}
      <AggregationForCurrentColumn scrollContainer={scrollRef.current} />
      {!readOnly && <SearchAndReplaceWrapper />}
    </Stack>
  );
};

// AFI: After the new footer is removed, all selectors can be moved directly into SuggestedOperationsActions component
const SuggestedOperationsActionsWrapper = () => {
  const suggestedOperationsOptions = useSelector(
    suggestedOperationsOptionsSelector
  );
  const suggestingOperations = useSelector(
    (state: State) => state.ui.suggestingOperations
  );
  const tuplesCount = useSelector(
    (state: State) =>
      getTableDatapoints(state)?.filter(dp => !isVirtualDatapoint(dp.id))
        .length ?? 0
  );
  const datapointPath = useSelector(datapointPathSelector);

  return (
    <SuggestedOperationsActions
      tuplesCount={tuplesCount}
      suggestedOperationsOptions={suggestedOperationsOptions}
      suggestingOperations={suggestingOperations}
      datapointPath={datapointPath}
    />
  );
};

const SEARCH_AND_REPLACE_DIALOG_WIDTH = 224;

const SearchAndReplaceWrapper = () => {
  const { currentColumn, columnRightClientX } = useSelector(
    (state: State) => state.searchAndReplace
  );

  const leftSearchPanelCoord = Math.max(
    0,
    columnRightClientX - SIDEBAR_WIDTH - SEARCH_AND_REPLACE_DIALOG_WIDTH
  );

  return (
    <>
      {!!currentColumn &&
        ReactDOM.createPortal(
          <Paper
            className={styles['SearchAndReplace']}
            style={{ left: leftSearchPanelCoord }}
          >
            <SearchAndReplace currentFooterColumn={currentColumn} />
          </Paper>,
          document.getElementById('footer')!
        )}
    </>
  );
};

export default makeResizable(Footer);
